import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTeacherToken } from '../hooks/useTeacherAuth.js';
import { useRoomChannel } from '../deck/useRoomChannel.js';
import { useDeckPosition } from '../deck/useDeckPosition.js';
import { useDeckKeys } from '../deck/useDeckKeys.js';
import { useSimRelay } from '../deck/camtags/simInput.js';
import { quizPhase, answerCount } from '../deck/quizPhase.js';
import { stopLabel } from '../deck/stopLabel.js';
import SlidePreview from '../deck/SlidePreview.jsx';
import { buildDeck } from '../constants/decks.js';
import { AvatarBadge } from '../constants/avatars.jsx';
import RemovePlayerButton from '../components/RemovePlayerButton.jsx';
import {
  ArrowLeftIcon, ArrowRightIcon, CloseIcon, LayersIcon,
  LightbulbIcon, QrCodeIcon, StarIcon, HandIcon, MaximizeIcon,
} from '../components/Icons.jsx';
import '../styles/control.css';

const MIN_WIDTH = 900;

/**
 * The instructor's control panel — the second screen. It runs the same
 * position hook as the projected deck, so an arrow press here and an arrow
 * press there are the same press; what differs is what is drawn around it.
 *
 * It is a landscape-only screen on purpose (see the width guard below): the
 * three columns exist so that nothing the instructor needs mid-lesson is
 * behind a tab, and folding them into one phone column would defeat that.
 * Its text is allowed to be smaller than the deck's, because nobody is
 * reading it from the back of a classroom — except the two numbers that get
 * glanced at mid-sentence, the slide count and the answered count.
 */
export default function TeacherControl() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const room = useRoomChannel(roomId);
  const [narrow, setNarrow] = useState(window.innerWidth < MIN_WIDTH);
  const [showSlides, setShowSlides] = useState(false);

  React.useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < MIN_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    if (!getTeacherToken() || room.authState === 'unauthenticated') {
      navigate('/teacher', { replace: true });
    }
  }, [room.authState, navigate]);

  const deck = useMemo(
    () => buildDeck(room.roomCode, room.questions),
    [room.roomCode, room.questions]
  );
  const stops = deck.stops;

  const { index, substep, lastCount, stop, nextStop, stepsOf, goRel, goTo } =
    useDeckPosition(room, stops, { isProjector: false });

  // The camera slide's demo mode is driven from the keyboard, and the
  // instructor's keyboard is focused here, not on the projector window.
  useSimRelay(stop?.kind === 'cam-tags');

  const overlay = room.position?.overlay ?? null;
  const toggleOverlay = useCallback(
    (kind) => room.actions.setOverlay(overlay === kind ? null : kind).catch(() => {}),
    [overlay, room]
  );

  useDeckKeys(
    {
      ArrowRight: () => goRel(1),
      PageDown: () => goRel(1),
      ' ': () => goRel(1),
      ArrowLeft: () => goRel(-1),
      PageUp: () => goRel(-1),
      o: () => setShowSlides((v) => !v),
      c: () => toggleOverlay('check'),
      q: () => toggleOverlay('qr'),
      Escape: () => setShowSlides(false),
    },
    (n) => goTo(n - 1)
  );

  if (room.authState !== 'ok') {
    return <div className="page"><p className="text-dim">กำลังตรวจสอบสิทธิ์...</p></div>;
  }

  if (narrow) {
    return (
      <div className="page control-too-narrow">
        <h1>แผงควบคุมต้องใช้จอแนวนอน</h1>
        <p className="text-dim">
          หมุนเครื่องเป็นแนวนอน หรือเปิดบนแท็บเล็ต/โน้ตบุ๊ก แล้วลองอีกครั้ง
        </p>
      </div>
    );
  }

  const findQuestion = (order) =>
    room.questions.find((q) => q.order_index === order) || null;

  const phase = quizPhase(room, stop);
  const { answered, total } = answerCount(room);
  const { understand, hands } = room.pulse;
  const roster = [...room.players].sort((a, b) => a.nickname.localeCompare(b.nickname, 'th'));

  const openProjector = () => {
    window.open(`/teacher/host/${roomId}`, `lc-projector-${roomId}`, 'noopener');
  };
  const projectorUrl = `${window.location.origin}/teacher/host/${roomId}`;

  return (
    <div className="control-root">
      <header className="control-top">
        <div className="control-lesson">
          <span className="control-unit">{deck.meta?.unit || 'บทเรียน'}</span>
          <strong>{(deck.meta?.title || '').replace(/\n/g, ' ')}</strong>
        </div>
        <div className="control-top-right">
          {!room.connected && <span className="control-offline">การเชื่อมต่อหลุด — กำลังเชื่อมต่อใหม่</span>}
          <span className="control-code num">{room.roomCode}</span>
          <button className="btn btn-ghost btn-sm" onClick={openProjector} title={projectorUrl}>
            <MaximizeIcon size={14} /> เปิดจอฉาย
          </button>
          <button className="control-icon-btn" onClick={() => navigate('/teacher')} title="ออก">
            <CloseIcon size={16} />
          </button>
        </div>
      </header>

      {/* Popup blockers swallow window.open without a word, and the projector
          is often a different machine anyway, so the address is always here
          to be copied or typed. */}
      <p className="control-projector-url">
        จอฉาย: <code>{projectorUrl}</code>
      </p>

      <div className="control-grid">
        <section className="control-col control-col-slides">
          <SlidePreview roomId={roomId} index={index} substep={substep} label="สไลด์ปัจจุบัน" />
          <SlidePreview
            roomId={roomId}
            index={nextStop ? index + 1 : null}
            label="สไลด์ถัดไป"
            empty="สไลด์สุดท้ายแล้ว"
          />
        </section>

        <section className="control-col control-col-notes">
          <div className="control-card control-quiz">
            <h2>คำถาม</h2>
            {phase === 'none' && <p className="control-empty">สไลด์นี้ไม่ใช่คำถาม</p>}
            {phase === 'closed' && (
              <button
                className="btn btn-gold control-big-btn"
                disabled={room.busy}
                onClick={() => room.actions.openQuestion(stop.order)}
              >
                เปิดให้ตอบ
              </button>
            )}
            {phase === 'open' && (
              <>
                <p className="control-answered num">
                  {answered}<span>/{total}</span>
                </p>
                <p className="control-answered-label">ตอบแล้ว</p>
                <button
                  className="btn btn-primary control-big-btn"
                  disabled={room.busy}
                  onClick={() => room.actions.reveal()}
                >
                  เฉลย
                </button>
              </>
            )}
            {phase === 'revealed' && (
              <p className="control-revealed">เฉลยแล้ว — กด → เพื่อไปต่อ</p>
            )}
          </div>
          <div className={`control-card control-notes ${stop?.notes ? '' : 'is-empty'}`}>
            <h2>โน้ตผู้สอน</h2>
            {stop?.notes ? (
              <p>{stop.notes}</p>
            ) : (
              <p className="control-empty">สไลด์นี้ยังไม่มีโน้ต</p>
            )}
          </div>

        </section>

        <section className="control-col control-col-class">
          <div className="control-card control-pulse">
            <h2>ความเข้าใจของสไลด์นี้</h2>
            <div className="control-meter">
              <div className="control-meter-ok" style={{ flexGrow: understand.ok || 0.001 }} />
              <div className="control-meter-confused" style={{ flexGrow: understand.confused || 0.001 }} />
            </div>
            <p className="control-meter-label">
              เข้าใจ <b className="num">{understand.ok}</b> · ยังงง <b className="num">{understand.confused}</b>
            </p>
            {hands.length > 0 && (
              <p className="control-hands">
                <HandIcon size={14} /> ยกมือ {hands.map((h) => h.nickname).join(', ')}
              </p>
            )}
          </div>

          <div className="control-card control-roster">
            <h2>ผู้เรียน <span className="num">{room.connectedCount}</span></h2>
            <ul>
              {roster.map((p) => (
                <li key={p.id} className={p.connected ? '' : 'is-offline'}>
                  <AvatarBadge avatar={p.avatar} size="sm" />
                  <span className="control-roster-name">{p.nickname}</span>
                  <span className="control-roster-stars num">{p.stars > 0 ? `⭐ ${p.stars}` : ''}</span>
                  <button
                    className="control-star-btn"
                    onClick={() => room.actions.awardStar(p.id)}
                    title={`ให้ดาว ${p.nickname}`}
                  >
                    <StarIcon size={14} />
                  </button>
                  <RemovePlayerButton player={p} onRemove={room.actions.kickPlayer} />
                </li>
              ))}
              {roster.length === 0 && <li className="control-empty">ยังไม่มีผู้เรียนเข้าห้อง</li>}
            </ul>
          </div>
        </section>
      </div>

      <footer className="control-bar">
        <button className="control-icon-btn" onClick={() => goRel(-1)} disabled={index === 0} title="ย้อนกลับ (←)">
          <ArrowLeftIcon size={18} />
        </button>
        <span className="control-count num">{index + 1} / {lastCount}</span>
        {/* Some slides answer an arrow with a reveal step rather than a new
            slide. Without this the instructor presses, sees the big picture
            stay put, and presses again — which is what makes the deck feel
            like it lags and then jumps two. */}
        {stepsOf(index) > 0 && (
          <span className="control-substep num" title="สไลด์นี้เปิดทีละขั้น">
            ขั้น {substep + 1}/{stepsOf(index) + 1}
          </span>
        )}
        <button
          className="control-icon-btn"
          onClick={() => goRel(1)}
          disabled={index === lastCount - 1}
          title="ถัดไป (→)"
        >
          <ArrowRightIcon size={18} />
        </button>
        <span className="control-bar-sep" />
        <button className="control-icon-btn" onClick={() => setShowSlides((v) => !v)} title="สไลด์ทั้งหมด (O)">
          <LayersIcon size={16} />
        </button>
        <button
          className={`control-icon-btn ${overlay === 'qr' ? 'is-on' : ''}`}
          onClick={() => toggleOverlay('qr')}
          title="QR เข้าเรียน บนจอฉาย (Q)"
        >
          <QrCodeIcon size={16} />
        </button>
        <button
          className={`control-icon-btn ${overlay === 'check' ? 'is-on' : ''}`}
          onClick={() => toggleOverlay('check')}
          title="เช็คความเข้าใจเต็มจอ บนจอฉาย (C)"
        >
          <LightbulbIcon size={16} />
        </button>
        <span className="control-bar-hint">{stopLabel(stop, findQuestion)}</span>
      </footer>

      {showSlides && (
        <div className="control-slides-drawer" onClick={() => setShowSlides(false)}>
          <div className="control-slides-panel" onClick={(e) => e.stopPropagation()}>
            <h2>สไลด์ทั้งหมด · {lastCount} จอ</h2>
            <ol>
              {stops.map((s, i) => (
                <li key={i}>
                  <button
                    className={i === index ? 'is-current' : ''}
                    onClick={() => {
                      goTo(i);
                      setShowSlides(false);
                    }}
                  >
                    <span className="control-slides-num">{i + 1}</span>
                    <span>{stopLabel(s, findQuestion)}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
